import {
  DEFAULT_MOVE_SPEED,
  MAX_CATCHUP_STEPS,
  type Mover,
  TICK_MS,
  type Vec2,
  accumulateSteps,
  cellCentre,
  distance,
  nearestReachable,
  stepDistance,
  stepWalker,
} from '@d2/core';

import { type Motion, placeAt, startMotion, walkTo } from './navigation';

/**
 * Where to draw the one entity whose input this client *does* hold.
 *
 * `ARCHITECTURE.md` splits the client's two jobs down the middle: it
 * "interpolat[es] remote entities behind a ~100 ms buffer and predict[s] only
 * its own movement using the identical `core` code". `./interpolation` is the
 * first half — samples in, a point out, no input to run. This is the second, and
 * it is the opposite problem: there is an input, it happened *here*, and a
 * player who watches their own click take a round trip to move thinks the game
 * is broken. So the click is walked immediately, out of the same `@d2/core`
 * functions the room will run as truth, and the server's answer is folded in
 * when it arrives.
 *
 * Nothing here connects to anything. A `Prediction` in, a `Prediction` out, time
 * as a parameter and no clock read anywhere — the networking pass wires it up
 * without changing a line, exactly as it will the interpolation buffer.
 *
 * ## The wire, and where it stops
 *
 * Three facts arrive per snapshot, and each is used for something different:
 *
 *   - **the position**, `float32` on the wire (`TODO.md` #112), which is truth
 *     about where the entity *was* on the tick that produced it;
 *   - **the destination the server accepted**, which is routinely not the cell
 *     that was clicked — a click on rock walks as close as it can (`TODO.md`
 *     #92), so this is the only way to learn where the walk is really going;
 *   - **`lastSequence`**, the click the server was answering. Written only for
 *     an intent it *applied*, so silence is a refusal (`TODO.md` #133).
 *
 * `AuthoritativeState` declares those structurally rather than importing
 * `PlayerState`, for the reason `./interpolation` declares `PositionSample`
 * itself: `apps/web` does not depend on `@d2/schema`, and a module that took a
 * `PlayerState` would need `@colyseus/schema` to be unit-testable at all. The
 * mapping is the caller's one-liner — `walking ? vec2(destinationX,
 * destinationY) : null`, which is also where the deliberate staleness of
 * `destinationX/Y` after a walk ends (`clearDestination`) is absorbed.
 *
 * ## The four decisions, and why
 *
 * **Below `CORRECTION_THRESHOLD_CELLS`, a disagreement is ignored; above it, the
 * client is snapped.** Not eased, and the reason is diagnostic before it is
 * aesthetic: while the netcode is this young a real desync has to be *visible*,
 * and a glide towards the server's position hides precisely the bug most worth
 * seeing. A smooth correction makes a broken predictor look like a working one.
 *
 * Three more things agree with that call. The corrections that survive the
 * threshold are *route*-shaped, not position-shaped — a destination the server
 * resolved elsewhere, a click it refused, a stall it simulated through — and
 * easing draws a straight line between two points that is not a line anything
 * walked: it cuts corners through rock, which is the one thing a renderer of
 * this game may never do. `ARCHITECTURE.md` is explicit that for the capped
 * catch-up "server state *is* the correction, and that is the general rule
 * rather than a special case". And the snap is affordable precisely because it
 * is rare: this project removed the things that cause a steady drip of small
 * corrections — players do not collide, monsters block rather than push, and a
 * blocked step "is not a correction: it is a *shorter step*, taken from the same
 * state the client already has" (`world.ts`).
 *
 * **A refused intent is retired by the next acknowledgement, or by a timeout.**
 * Sequences only ever increase and the room refuses anything at or below
 * `lastSequence` as stale, so the moment the server answers click 7 every
 * pending click below it is dead — applied never, and unable to be applied now.
 * That covers every refusal except the last one in flight, which no
 * acknowledgement will ever mention: for that one there is `INTENT_TIMEOUT_TICKS`
 * of predicted time, after which the client stops holding the click and lets the
 * next snapshot say where it really is. `TODO.md` #133 is the boundary — a
 * refused *first* click leaves `lastSequence` at zero, which is also exactly
 * what "still in flight" looks like, so the timeout is the only thing that can
 * tell them apart and it is why the timeout is generous rather than tight.
 *
 * **Walking somewhere other than the clicked cell is ordinary.** The server
 * resolves a click with `nearestReachable` before pathing, so the accepted
 * destination differs from the clicked one whenever the click was on rock, in a
 * sealed cavern, or in a gap this body does not fit. Two consequences: the
 * client resolves its own clicks the same way, out of the same core function, so
 * the common case is predicted rather than corrected; and an acknowledgement
 * that names a *different* destination re-routes the prediction where it stands,
 * without a snap and without treating the difference as an error.
 *
 * **A capped catch-up needs no special handling.** A snapshot from a tick the
 * client never predicted — the backgrounded tab core refuses to replay past
 * `MAX_CATCHUP_STEPS` — is adopted whole and the client's own tick counter is
 * re-anchored to it. There is nothing to compare and nothing to preserve, which
 * is the general rule again rather than a branch that fights it.
 *
 * ## The timeline
 *
 * One counter, `Prediction.tick`, on the server's own scale: seeded from a
 * snapshot, advanced by one per simulation step the client runs, and the key
 * every predicted position is filed under. A snapshot for tick *T* is therefore
 * compared against what *this client* thought at *T*, never against what it
 * thinks now — the client is legitimately ahead of the server by the round trip,
 * and comparing those two would "correct" the whole point of predicting.
 *
 * Mapping the browser's clock onto that scale is the caller's job and belongs to
 * the networking pass (`TODO.md` #160, #183), exactly as it is for the
 * interpolation buffer. Until it lands, a caller passing the raw server tick is
 * a client whose history is offset by the round trip.
 *
 * Positions are in **cell coordinates**, like all of core; the renderer projects.
 */

/**
 * The value `PlayerState.lastSequence` carries before the server has applied any
 * intent — `@d2/schema`'s `NO_INTENT_APPLIED`, restated here because `apps/web`
 * does not depend on that package (`TODO.md` #185).
 *
 * Zero is reserved on the wire so that a fresh player and an acknowledged first
 * click cannot be confused, which is the only reason the client may treat "no
 * acknowledgement yet" as a number at all.
 */
export const NO_INTENT_ACKNOWLEDGED = 0;

/** The first sequence a client may send. Counting starts above the reserved zero. */
export const FIRST_INTENT_SEQUENCE = 1;

/**
 * How far the prediction and the server may disagree before the client is
 * **snapped** onto the server's position: a quarter of one simulation step,
 * 0.05 cells.
 *
 * This number is a **visibility decision, not a cosmetic one**, and the snap
 * above it is deliberate: anything that survives this threshold is a genuine
 * disagreement between two machines that are supposed to be running identical
 * code, and it is meant to be seen. Easing the correction over a few frames
 * would look better and would hide exactly the desync that most needs finding.
 * If a future reader is here to "improve" the jump into a lerp, that is the
 * decision being reversed — make it knowingly, and only once the netcode has
 * earned the benefit of the doubt (`TODO.md` #180).
 *
 * Derived from core's own step rather than typed as a number, so it stays a
 * fraction of a step if the tick rate or the walk speed moves. The two ends it
 * has to sit between are both fixed by decisions elsewhere:
 *
 *   - **Above the wire's quantisation**, by three to four orders of magnitude.
 *     `PlayerState.x/y` are `float32`, so an authoritative position is
 *     `Math.fround` of the one the server simulated — about 1e-6 of a cell out
 *     at dungeon scale (`TODO.md` #112). A threshold anywhere near that would
 *     have a client predicting *perfectly* correcting itself twenty times a
 *     second, forever.
 *   - **Below one whole step.** A client that has genuinely lost or gained a
 *     step against the server is 0.2 cells out and must be pulled back into
 *     line; a threshold above a step would let that hide indefinitely, and the
 *     drift a threshold is *for* is the rounding, not a missing tick.
 *
 * A quarter step also happens to be about two screen pixels at the current
 * projection, so nothing that survives it can be missed and nothing that is
 * ignored can be seen.
 */
export const CORRECTION_THRESHOLD_CELLS = stepDistance(DEFAULT_MOVE_SPEED) / 4;

/**
 * How long the client keeps predicting a click nothing has answered. **Twenty
 * ticks — one second of simulated time.**
 *
 * A placeholder, openly, like `DEFAULT_MOVE_SPEED` and the separation constants
 * before it (`TODO.md` #181): the honest value is a multiple of the measured
 * round trip, and nothing measures the round trip yet. Twenty ticks is chosen
 * against the asymmetry rather than against a network — firing early costs a
 * hitch on a slow connection, where an acknowledgement lands just after the
 * client stopped predicting it and the next snapshot puts the walk back; firing
 * late costs a rubber-band, where the client keeps walking a click that never
 * happened and is pulled back every snapshot until it gives up. Twenty ticks is
 * long enough that no healthy connection reaches it (an acknowledgement is one
 * to three ticks away) and short enough that a refusal cannot be mistaken for
 * play.
 *
 * Counted in *predicted* ticks, not wall-clock milliseconds, so it is one
 * timeline with the rest of the module and a stalled tab — which is not
 * predicting, and therefore not rubber-banding — does not burn it down.
 */
export const INTENT_TIMEOUT_TICKS = 20;

/** The same patience in milliseconds, for callers that reason in time. */
export const INTENT_TIMEOUT_MS = INTENT_TIMEOUT_TICKS * TICK_MS;

/**
 * How much predicted history is kept behind the newest predicted tick.
 *
 * `MAX_CATCHUP_STEPS` — core's own answer to "how far behind may a client fall
 * before catching up is the wrong idea", and the same bound `./interpolation`
 * retains samples by. Older than that and a snapshot cannot be compared with
 * anything, which is exactly the case `ARCHITECTURE.md` says to answer by
 * adopting server state rather than by keeping more state around to reconstruct
 * it.
 */
export const PREDICTION_HISTORY_TICKS = MAX_CATCHUP_STEPS;

/**
 * What the server says about the local player, on one tick.
 *
 * Structural, not `PlayerState` — see the module note. `destination` folds
 * `walking` and `destinationX/Y` into the one fact the client needs: the cell
 * this walk ends on, or `null` when the server is not walking this entity.
 */
export interface AuthoritativeState {
  /** `WorldState.tick` — the server tick this snapshot was produced on. Whole and non-negative. */
  readonly tick: number;
  /** Continuous position in cell coordinates. Truth. */
  readonly position: Vec2;
  /** The **cell** the server accepted, or `null` when this entity is not walking. */
  readonly destination: Vec2 | null;
  /** The sequence of the last intent the server *applied*, or `NO_INTENT_ACKNOWLEDGED`. */
  readonly lastSequence: number;
}

/**
 * A click the client has predicted and the server has not yet answered.
 *
 * `sequence` and `target` are the whole of the `moveTo` payload, so this value
 * is what the caller sends. `tick` is local bookkeeping: the predicted tick the
 * click was applied on, which is what lets a replay re-apply it *when* it
 * happened rather than all at once at the start.
 */
export interface PendingIntent {
  readonly sequence: number;
  /** The clicked cell, exactly as sent. Not where the entity will end up. */
  readonly target: Vec2;
  /** The predicted tick this click was applied on. */
  readonly tick: number;
}

/** Where the client thought it was on one predicted tick. */
export interface PredictedPosition {
  readonly tick: number;
  readonly position: Vec2;
}

/**
 * The local player's predicted movement, and everything needed to reconcile it.
 *
 * Immutable, like the interpolation buffer it sits beside: every function here
 * returns a new value, so a frame can never observe a half-applied correction.
 */
export interface Prediction {
  /** The drawn state: core's walker, plus the unspent milliseconds of the frame clock. */
  readonly motion: Motion;
  /** The client's own count of simulation steps, on the server's scale. */
  readonly tick: number;
  /** Clicks sent and not yet acknowledged, oldest first. */
  readonly pending: readonly PendingIntent[];
  /** The newest sequence the server has said it applied. */
  readonly acknowledged: number;
  /** The sequence the next click will carry. */
  readonly nextSequence: number;
  /** Predicted positions, oldest first, strictly increasing `tick`. */
  readonly history: readonly PredictedPosition[];
}

/** A click, predicted: the new state, and the intent to put on the wire. */
export interface PredictedIntent {
  readonly prediction: Prediction;
  readonly intent: PendingIntent;
}

const assertTick = (tick: number): void => {
  // Phrased as a negated comparison so NaN — a tick that arrived as a bad
  // subtraction — is rejected along with the negatives.
  if (!(Number.isInteger(tick) && tick >= 0)) {
    throw new RangeError(`A snapshot's tick must be a whole, non-negative number, received ${tick}`);
  }
};

/** Cancels the walk in progress, leaving the entity exactly where it stands. */
const stop = (motion: Motion): Motion => placeAt(motion, motion.walker.position);

/**
 * The client's own click, resolved the way the room resolves it.
 *
 * `nearestReachable` first, then the ordinary route — the same two steps, in the
 * same order, that `world.ts`'s `orderMove` takes. That is what makes the
 * common case a *prediction* rather than a correction: a click on rock walks as
 * close as it can on both sides, so the client does not stand still watching the
 * server walk it somewhere.
 *
 * An entity standing somewhere it could not stand can reach nothing, and a
 * target with no route to it is core's returned `null` rather than a throw. Both
 * mean the same thing here — this order goes nowhere, so the entity stops.
 */
const order = (mover: Mover, motion: Motion, target: Vec2): Motion => {
  const destination = nearestReachable(mover.clearance.grid, motion.walker.position, target, mover);
  // One refusal, not two: whatever `nearestReachable` picked, it reached by the
  // movement rule A* is about to search with, so the second `null` is the same
  // outcome arriving by a route the guarantee says cannot be taken.
  const routed = destination === null ? null : walkTo(mover, motion, destination);

  return routed ?? stop(motion);
};

/**
 * Points the walk at the destination the *server* accepted.
 *
 * No `nearestReachable`: the server has already resolved this cell, and
 * re-resolving it would be the client second-guessing an answer it was given. A
 * destination that agrees with the route already being walked returns the very
 * same motion, so an acknowledgement costs no pathfinding and no float churn in
 * the overwhelmingly common case where the server accepted what was clicked.
 */
const retarget = (mover: Mover, motion: Motion, destination: Vec2 | null): Motion => {
  const walkingTo = motion.walker.path.at(-1) ?? null;

  if (destination === null) return walkingTo === null ? motion : stop(motion);

  const stopAt = cellCentre(destination);
  if (walkingTo !== null && walkingTo.x === stopAt.x && walkingTo.y === stopAt.y) return motion;

  return walkTo(mover, motion, destination) ?? stop(motion);
};

/**
 * One simulation step, and nothing else.
 *
 * The accumulator's remainder is deliberately untouched: it is the phase of the
 * *browser's* frame clock against the simulation, so a replay of whole ticks
 * must not disturb it. `advancePrediction` owns the accumulator; this is the
 * step both it and the replay share.
 */
const stepMotion = (motion: Motion): Motion => ({ ...motion, walker: stepWalker(motion.walker).walker });

const applyOrders = (mover: Mover, motion: Motion, intents: readonly PendingIntent[]): Motion =>
  intents.reduce((current, intent) => order(mover, current, intent.target), motion);

const retain = (history: readonly PredictedPosition[], oldestTick: number): readonly PredictedPosition[] =>
  history.filter((step) => step.tick >= oldestTick);

/** The player, standing where the server says, on the tick the server said it. */
export const createPrediction = (
  mover: Mover,
  snapshot: AuthoritativeState,
  speed: number = DEFAULT_MOVE_SPEED,
): Prediction => {
  assertTick(snapshot.tick);

  return {
    motion: retarget(mover, startMotion(snapshot.position, speed), snapshot.destination),
    tick: snapshot.tick,
    pending: [],
    acknowledged: snapshot.lastSequence,
    nextSequence: FIRST_INTENT_SEQUENCE,
    history: [{ tick: snapshot.tick, position: snapshot.position }],
  };
};

/** Where to draw the local player right now. */
export const predictedPosition = (prediction: Prediction): Vec2 => prediction.motion.walker.position;

/**
 * A click: walked immediately, and remembered until the server answers it.
 *
 * The walk starts on this frame — that is the whole point of the module — and
 * the intent is held by sequence number so that the answer, whenever it comes,
 * can be matched to the click that asked for it. A click the client cannot route
 * is still numbered and still handed back: the server decides what happens, not
 * the client, and a prediction of "nothing" is a prediction like any other.
 */
export const predictMoveTo = (mover: Mover, prediction: Prediction, target: Vec2): PredictedIntent => {
  const intent: PendingIntent = { sequence: prediction.nextSequence, target, tick: prediction.tick };

  return {
    prediction: {
      ...prediction,
      motion: order(mover, prediction.motion, target),
      pending: [...prediction.pending, intent],
      nextSequence: intent.sequence + 1,
    },
    intent,
  };
};

/**
 * Spends `elapsedMs` of real time on the prediction, in whole simulation steps.
 *
 * The frame's own duration never reaches the simulation: `accumulateSteps` banks
 * it and hands back only exact 50 ms steps, capped at `MAX_CATCHUP_STEPS`, so a
 * stutter cannot turn into a different position and a backgrounded tab is not
 * replayed. Every step taken is a tick counted and a position filed, because a
 * snapshot is compared against what the client thought *on that tick*.
 *
 * It is also where a click gives up: an intent nothing has acknowledged within
 * `INTENT_TIMEOUT_TICKS` is dropped, which merely stops it being replayed. The
 * walk it started is not unwound here — the next snapshot does that, because
 * server state is the correction rather than something to pre-empt.
 */
export const advancePrediction = (prediction: Prediction, elapsedMs: number): Prediction => {
  const budget = accumulateSteps(prediction.motion.remainder, elapsedMs);

  let motion = prediction.motion;
  let tick = prediction.tick;
  let history = prediction.history;

  for (let step = 0; step < budget.steps; step += 1) {
    motion = stepMotion(motion);
    tick += 1;
    history = [...history, { tick, position: motion.walker.position }];
  }

  return {
    ...prediction,
    motion: { ...motion, remainder: budget.remainder },
    tick,
    history: retain(history, tick - PREDICTION_HISTORY_TICKS),
    pending: prediction.pending.filter((intent) => tick - intent.tick <= INTENT_TIMEOUT_TICKS),
  };
};

/**
 * Takes the server's word for it: the position at `snapshot.tick`, the route it
 * accepted, and every click still unanswered replayed on top.
 *
 * The correction runs forward from the snapshot rather than backwards from now.
 * Each unacknowledged click is re-applied on the tick it was originally applied
 * on, and a step is spent between ticks, so the replay reproduces the walk it
 * replaces — a click made three ticks after the snapshot turns three ticks
 * after it, not immediately.
 */
const adopt = (mover: Mover, prediction: Prediction, snapshot: AuthoritativeState): Prediction => {
  const from = snapshot.tick;
  // Behind the server: there is nothing to replay, and the client's clock
  // re-anchors to the tick it was handed.
  const to = Math.max(prediction.tick, from);

  let motion = retarget(mover, placeAt(prediction.motion, snapshot.position), snapshot.destination);
  motion = applyOrders(
    mover,
    motion,
    prediction.pending.filter((intent) => intent.tick <= from),
  );
  let history: readonly PredictedPosition[] = [{ tick: from, position: snapshot.position }];

  for (let tick = from + 1; tick <= to; tick += 1) {
    motion = stepMotion(motion);
    motion = applyOrders(
      mover,
      motion,
      prediction.pending.filter((intent) => intent.tick === tick),
    );
    history = [...history, { tick, position: motion.walker.position }];
  }

  return { ...prediction, motion, tick: to, history };
};

/**
 * Folds one authoritative snapshot into the prediction.
 *
 * Three outcomes, and the whole of the reconciliation policy:
 *
 *   - **Nothing predicted that tick.** Either the client is behind the server —
 *     a capped catch-up, a tab that was asleep — in which case server state is
 *     the correction and the clock re-anchors to it; or the snapshot is older
 *     than anything still held, which one TCP connection per room cannot
 *     actually deliver and which carries no information anyway, so it is
 *     dropped. Defensive, like the interpolation buffer's out-of-order guard.
 *   - **They agree, within `CORRECTION_THRESHOLD_CELLS`.** The prediction is
 *     left exactly as it is — the same object, so the client's float64 walker
 *     never absorbs the wire's `float32` rounding. The only thing that may still
 *     change is the *route*: a snapshot that acknowledges a click, with no newer
 *     click outstanding, points the walk at the destination the server actually
 *     accepted. That re-route moves nobody; it is how walk-as-close-as-you-can
 *     stops being a divergence and becomes agreement.
 *   - **They disagree.** The client is put where the server says and everything
 *     unanswered is replayed forward from there.
 *
 * An acknowledgement retires every pending click at or below it, whether or not
 * that click was the one applied: sequences only increase and the room refuses
 * anything at or below `lastSequence`, so an older one can never be applied
 * afterwards. That is the refusal channel, and it costs no message.
 */
export const reconcile = (mover: Mover, prediction: Prediction, snapshot: AuthoritativeState): Prediction => {
  assertTick(snapshot.tick);

  const settled: Prediction = {
    ...prediction,
    acknowledged: Math.max(prediction.acknowledged, snapshot.lastSequence),
    pending: prediction.pending.filter((intent) => intent.sequence > snapshot.lastSequence),
  };

  const predicted = prediction.history.find((step) => step.tick === snapshot.tick);
  if (predicted === undefined) {
    return snapshot.tick > prediction.tick ? adopt(mover, settled, snapshot) : settled;
  }

  if (distance(predicted.position, snapshot.position) > CORRECTION_THRESHOLD_CELLS) {
    return adopt(mover, settled, snapshot);
  }

  const answered = snapshot.lastSequence > prediction.acknowledged && settled.pending.length === 0;

  return {
    ...settled,
    motion: answered ? retarget(mover, prediction.motion, snapshot.destination) : prediction.motion,
    history: retain(prediction.history, snapshot.tick),
  };
};
